import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./WhyUs.css";

/*
 * Accueil — Le Blog, design premium (audit section 08).
 * Les articles proviennent de la même source que Blog.jsx : blog.posts.
 * Le composant reste WhyUs pour préserver l'intégration existante.
 */
const LABELS = {
  fr: {
    label: "Le blog",
    title: "Actualités, innovations et conseils.",
    readArticle: "Lire l'article",
    allArticles: "Voir tous les articles",
    noPosts: "Aucun article disponible pour le moment."
  },
  en: {
    label: "The blog",
    title: "News, innovation and insights.",
    readArticle: "Read article",
    allArticles: "View all articles",
    noPosts: "No articles available at the moment."
  }
};

function isUsablePost(post) {
  return (
    post &&
    typeof post === "object" &&
    typeof post.slug === "string" && post.slug.trim() !== "" &&
    typeof post.title === "string" && post.title.trim() !== "" &&
    typeof post.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(post.date) &&
    Number.isFinite(Date.parse(`${post.date}T12:00:00`)) &&
    typeof post.image === "string" && post.image.trim() !== ""
  );
}

function formatPostDate(date, locale) {
  return new Date(`${date}T12:00:00`).toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
}

function ArticleCard({ post, featured, readArticle, locale }) {
  const url = `/blog/${post.slug}`;

  return (
    <article
      className={`unitech-home-blog__card${featured ? " unitech-home-blog__card--featured" : ""}`}
      itemScope
      itemType="https://schema.org/BlogPosting"
    >
      <NavLink
        to={url}
        className="unitech-home-blog__image-link"
        aria-label={`${readArticle} : ${post.title}`}
      >
        <img
          src={post.image}
          alt=""
          className="unitech-home-blog__image"
          loading="lazy"
          decoding="async"
          width="720"
          height="480"
        />
      </NavLink>

      <div className="unitech-home-blog__card-body">
        <div className="unitech-home-blog__meta">
          {post.category && (
            <span className="unitech-home-blog__category">{post.category}</span>
          )}
          <span className="unitech-home-blog__meta-separator" aria-hidden="true">·</span>
          <time dateTime={post.date} className="unitech-home-blog__date">
            {formatPostDate(post.date, locale)}
          </time>
        </div>

        <h3 className="unitech-home-blog__article-title" itemProp="headline">
          <NavLink to={url} className="unitech-home-blog__article-link">
            {post.title}
          </NavLink>
        </h3>

        <NavLink to={url} className="unitech-home-blog__read-link">
          <span>{readArticle}</span>
          <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </NavLink>
      </div>
    </article>
  );
}

export default function WhyUs() {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = LABELS[language] || LABELS.fr;
  const dateLocale = language === "fr" ? "fr-FR" : "en-GB";

  const posts = useMemo(() => {
    const data = t("blog.posts", { returnObjects: true });
    if (!data || typeof data !== "object") return [];

    const list = Array.isArray(data) ? data : Object.values(data);
    return list
      .filter(isUsablePost)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 3);
  }, [t, i18n.resolvedLanguage, i18n.language]);

  const label = t("whyus.blog.label", { defaultValue: fallback.label });
  const title = t("whyus.blog.title", { defaultValue: fallback.title });
  const readArticle = t("whyus.blog.readArticle", {
    defaultValue: fallback.readArticle
  });
  const allArticles = t("whyus.blog.allArticles", {
    defaultValue: fallback.allArticles
  });
  const noPosts = t("whyus.blog.noPosts", {
    defaultValue: fallback.noPosts
  });

  return (
    <section className="unitech-home-blog" aria-labelledby="unitech-home-blog-title">
      <div className="unitech-home-blog__container">
        <header className="unitech-home-blog__header">
          <div className="unitech-home-blog__heading">
            <p className="unitech-home-blog__eyebrow">{label}</p>
            <h2 id="unitech-home-blog-title" className="unitech-home-blog__title">
              {title}
            </h2>
          </div>
          <NavLink to="/blog" className="unitech-home-blog__all-link">
            <span>{allArticles}</span>
            <span aria-hidden="true">↗</span>
          </NavLink>
        </header>

        {posts.length > 0 ? (
          <div className={`unitech-home-blog__grid unitech-home-blog__grid--count-${posts.length}`}>
            {posts.map((post, index) => (
              <ArticleCard
                key={post.slug}
                post={post}
                featured={index === 0}
                readArticle={readArticle}
                locale={dateLocale}
              />
            ))}
          </div>
        ) : (
          <p className="unitech-home-blog__empty">{noPosts}</p>
        )}
      </div>
    </section>
  );
}
